import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';
import type { PetitionStatus } from '@change-corgi/middle-end-gql-schema';

import { useCurrentUserId } from 'src/app/shared/hooks/session';
import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { DecisionMakerResponsePageCombinedState, DecisionMakerResponsePagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { DecisionMakerResponsePageDataContextProvider } from './pageData';
import { DecisionMakerResponsePageStateContextProvider } from './pageState';

export type ErrorHandlerProps = { context: ErrorStateDetails<DecisionMakerResponsePageCombinedState> };

type Props = {
	petitionSlugOrId: string;
	responseId: string;
	prefetchedData: DecisionMakerResponsePagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export const DecisionMakerResponsePageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	petitionSlugOrId,
	responseId,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(petitionSlugOrId, responseId, prefetchedData);
	const userId = useCurrentUserId();

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	return (
		<DecisionMakerResponsePageDataContextProvider pageData={pageData}>
			<DecisionMakerResponsePageStateContextProvider>
				<TrackingContext
					properties={{
						dm_response_id: `dm_response_${pageData.data.decisionMakerResponse.id}`,
						petition_id: `petition_${pageData.data.decisionMakerResponse.petition.id}`,
						promotable: pageData.data.decisionMakerResponse.petition.promotable,
						signed: pageData.data.petitionSigned,
						own_petition: pageData.data.decisionMakerResponse.petition.user.id === userId,
						petition_status: getTrackingPetitionStatus(pageData.data.decisionMakerResponse.petition.status),
					}}
				>
					{children}
				</TrackingContext>
			</DecisionMakerResponsePageStateContextProvider>
		</DecisionMakerResponsePageDataContextProvider>
	);
};

function getTrackingPetitionStatus(status: PetitionStatus) {
	switch (status) {
		case 'VICTORY':
			return 'victory';
		case 'CLOSED':
			return 'closed';
		default:
			return 'active';
	}
}
